import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Tooltip,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom
} from '@mui/material';
import { 
  EmojiEvents, 
  Star, 
  TrendingUp, 
  Search, 
  RateReview, 
  Group, 
  LocalFireDepartment,
  MovieFilter,
  Refresh,
  Info,
  ExpandMore,
  ExpandLess,
  Person,
  WorkspacePremium,
  Movie,
  Tv,
  Theaters,
  VideoLibrary
} from '@mui/icons-material';
import { achievementService } from '../../services/achievementService';
import { getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

const LEADERBOARD_CATEGORIES = {
  ALL: {
    label: 'Overall Champions',
    icon: EmojiEvents,
    description: 'Top achievers across all categories',
    achievementType: null
  },
  MOVIE_BUFF: {
    label: 'Movie Buffs',
    icon: Movie,
    description: 'Most movies watched',
    achievementType: 'Movie Buff'
  },
  TOP_REVIEWERS: {
    label: 'Top Reviewers',
    icon: RateReview,
    description: 'Most reviews written',
    achievementType: 'First Review'
  },
  INFLUENCERS: {
    label: 'Influencers',
    icon: Group,
    description: 'Most followed users',
    achievementType: 'Influencer'
  },
  THE_OG: {
    label: 'The OGs',
    icon: Star,
    description: 'Watched special movies',
    achievementType: 'OG'
  },
  EARLY_BIRDS: {
    label: 'Early Birds',
    icon: TrendingUp,
    description: 'Early content discoverers',
    achievementType: 'Early Bird'
  },
  CONTENT_CREATORS: {
    label: 'Content Creators',
    icon: RateReview,
    description: 'Most posts created',
    achievementType: 'Content Creator'
  }
};

const AchievementLeaderboards = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [leaderboards, setLeaderboards] = useState({});
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [lastRecalculate, setLastRecalculate] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadLeaderboard(selectedCategory);
    loadLastRecalculate();
    checkCurrentUser();
  }, [selectedCategory]);

  const checkCurrentUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      // User not authenticated - this is fine for public leaderboards
      setUser(null);
    }
  };

  const loadLeaderboard = async (category) => {
    // Always reload to get fresh data with enhancements
    // if (leaderboards[category]) {
    //   return;
    // }
    
    try {
      setLoading(true);
      const categoryConfig = LEADERBOARD_CATEGORIES[category];
      const data = await achievementService.getLeaderboard(
        categoryConfig.achievementType, 
        'ALL_TIME', 
        10
      );
      
      setLeaderboards(prev => ({
        ...prev,
        [category]: data
      }));
      setError(null);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      // Check if it's a deployment/authorization issue
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError('Leaderboards are temporarily unavailable during system updates. Please try again in a few minutes.');
      } else {
        setError('Failed to load leaderboard. Please try again later.');
      }
      // Set empty array to show "no data" message instead of loading forever
      setLeaderboards(prev => ({
        ...prev,
        [category]: []
      }));
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return 'primary.main';
    }
  };

  const getRankGradient = (rank) => {
    switch (rank) {
      case 1: return 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)';
      case 2: return 'linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%)';
      case 3: return 'linear-gradient(135deg, #CD7F32 0%, #8D5524 100%)';
      default: return 'none';
    }
  };

  const handleUserClick = (entry) => {
    if (entry.username) {
      navigate(`/profile/${entry.username}`);
    }
  };

  const handleRecalculateAchievements = async () => {
    if (!user?.userId) return;
    
    // Check daily limit for non-admin users
    const isAdmin = user.signInUserSession?.accessToken?.payload?.['cognito:groups']?.includes('admin');
    const now = new Date();
    const today = now.toDateString();
    
    if (!isAdmin && lastRecalculate && lastRecalculate.toDateString() === today) {
      setError('You can only recalculate achievements once per day');
      return;
    }
    
    try {
      setRecalculating(true);
      setError(null);
      
      // Trigger achievement recalculation
      await achievementService.recalculateUserAchievements(user.userId);
      
      // Update last recalculate time
      localStorage.setItem('lastAchievementRecalculate', now.toISOString());
      setLastRecalculate(now);
      
      // Clear cached leaderboard data to force refresh
      setLeaderboards({});
      await loadLeaderboard(selectedCategory);
      
    } catch (err) {
      console.error('Error recalculating achievements:', err);
      setError('Failed to recalculate achievements');
    } finally {
      setRecalculating(false);
    }
  };

  const canRecalculate = () => {
    if (!user?.userId) return false;
    
    const isAdmin = user.signInUserSession?.accessToken?.payload?.['cognito:groups']?.includes('admin');
    if (isAdmin) return true;
    
    const today = new Date().toDateString();
    return !lastRecalculate || lastRecalculate.toDateString() !== today;
  };

  const loadLastRecalculate = () => {
    const stored = localStorage.getItem('lastAchievementRecalculate');
    if (stored) {
      setLastRecalculate(new Date(stored));
    }
  };

  const currentLeaderboard = leaderboards[selectedCategory] || [];
  const categoryConfig = LEADERBOARD_CATEGORIES[selectedCategory];
  const IconComponent = categoryConfig?.icon || EmojiEvents;

  return (
    <Box
      sx={{
        '& @keyframes shimmer': {
          '0%': { backgroundPosition: '200% 0' },
          '100%': { backgroundPosition: '-200% 0' }
        },
        '& @keyframes slide': {
          '0%': { left: '-100%' },
          '100%': { left: '100%' }
        }
      }}
    >
      {/* Rewards Banner */}
      <Card 
        sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }}
        />
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Box
              sx={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EmojiEvents sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                🎉 Compete for Glory!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Leading members will receive exclusive rewards in future updates
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip 
              label="🥇 Top 3 get special recognition" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }} 
            />
            <Chip 
              label="🎁 Exclusive rewards coming soon" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }} 
            />
            <Chip 
              label="⭐ Build your reputation" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }} 
            />
          </Box>
        </CardContent>
      </Card>

      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={isMobile ? "flex-start" : "center"} 
        mb={3}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
            🏆 Achievement Leaderboards ({Object.keys(LEADERBOARD_CATEGORIES).length} Categories)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Scroll through categories to explore different achievement rankings
          </Typography>
        </Box>
        
        {user?.userId && (
          <Box display="flex" alignItems="center" gap={1} width={isMobile ? "100%" : "auto"}>
            <Tooltip title="Get help about achievement recalculation">
              <IconButton size="small" onClick={() => setShowHelp(!showHelp)}>
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              onClick={handleRecalculateAchievements}
              disabled={recalculating || !canRecalculate()}
              startIcon={recalculating ? <CircularProgress size={16} /> : <Refresh />}
              sx={{ 
                minWidth: isMobile ? "auto" : 140,
                flex: isMobile ? 1 : "none"
              }}
            >
              {recalculating ? 'Updating...' : 'Recalculate'}
            </Button>
          </Box>
        )}
      </Box>
      
      {lastRecalculate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Last recalculated: {lastRecalculate.toLocaleString()}
          {!canRecalculate() && ' (Next recalculation available tomorrow)'}
        </Alert>
      )}
      
      <Collapse in={showHelp}>
        <Card sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Info color="primary" />
              <Typography variant="h6">Achievement Recalculation Help</Typography>
              <IconButton size="small" onClick={() => setShowHelp(false)}>
                <ExpandLess />
              </IconButton>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                💡 When to Use Recalculation:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • After watching movies, writing reviews, or gaining followers<br/>
                • When achievements seem outdated or missing<br/>
                • At the end of your session after multiple activities
              </Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                ⚠️ Important Warnings:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • <strong>Daily Limit:</strong> Regular users can only recalculate once per 24 hours<br/>
                • <strong>Processing Time:</strong> Takes 10-30 seconds - don't navigate away<br/>
                • <strong>No Guarantee:</strong> Only updates achievements you've actually earned
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Most achievements update automatically. Use manual recalculation only when needed!
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Collapse>
      
      <Tabs 
        value={selectedCategory} 
        onChange={(e, newValue) => setSelectedCategory(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ 
          mb: 3,
          '& .MuiTab-root': {
            minWidth: isMobile ? 100 : 140,
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            whiteSpace: 'nowrap'
          },
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
              opacity: 0.3
            }
          }
        }}
      >
        {Object.entries(LEADERBOARD_CATEGORIES).map(([key, config]) => {
          const TabIcon = config.icon;
          return (
            <Tab 
              key={key}
              value={key} 
              label={config.label}
              icon={<TabIcon />}
              iconPosition="start"
            />
          );
        })}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      <Card>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconComponent color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>{categoryConfig?.label}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {categoryConfig?.description}
          </Typography>
          
          {loading ? (
            <Box textAlign="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : currentLeaderboard.length === 0 ? (
            <Alert severity={error ? "warning" : "info"}>
              {error || "No data available for this category yet. Start unlocking achievements to see rankings!"}
            </Alert>
          ) : (
            <List>
              {currentLeaderboard.map((entry, index) => (
                <Zoom in={true} timeout={300 + index * 100} key={entry.userId}>
                  <ListItem 
                    divider={index < currentLeaderboard.length - 1}
                    onClick={() => handleUserClick(entry)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      mb: 1,
                      background: entry.rank <= 3 ? getRankGradient(entry.rank) : 'transparent',
                      backgroundSize: '200% 200%',
                      animation: entry.rank <= 3 ? 'shimmer 3s ease-in-out infinite' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: entry.rank <= 3 ? 'rgba(255, 255, 255, 0.1)' : 'action.hover',
                        transform: 'translateY(-2px)',
                        boxShadow: entry.rank <= 3 
                          ? `0 8px 25px rgba(${entry.rank === 1 ? '255, 215, 0' : entry.rank === 2 ? '192, 192, 192' : '205, 127, 50'}, 0.3)`
                          : theme.shadows[4]
                      },
                      '&::before': entry.rank <= 3 ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'slide 2s infinite'
                      } : {},
                      transition: 'all 0.3s ease',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      py: isMobile ? 2 : 1.5,
                      px: 2
                    }}
                  >
                    {/* Rank Badge */}
                    <Box
                      sx={{
                        position: entry.rank <= 3 ? 'absolute' : 'relative',
                        top: entry.rank <= 3 ? -8 : 'auto',
                        right: entry.rank <= 3 ? -8 : 'auto',
                        zIndex: 2,
                        order: isMobile ? -1 : 'initial',
                        mb: isMobile ? 1 : 0
                      }}
                    >
                      <Chip
                        label={getRankIcon(entry.rank)}
                        size={entry.rank <= 3 ? "medium" : "small"}
                        sx={{
                          bgcolor: getRankColor(entry.rank),
                          color: entry.rank <= 3 ? 'black' : 'white',
                          fontWeight: 700,
                          fontSize: entry.rank <= 3 ? '1rem' : '0.75rem',
                          boxShadow: entry.rank <= 3 ? theme.shadows[6] : 'none',
                          border: entry.rank <= 3 ? '2px solid rgba(255,255,255,0.3)' : 'none'
                        }}
                      />
                    </Box>

                    {/* User Avatar with Badge */}
                    <ListItemAvatar sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0, position: 'relative' }}>
                      <Avatar
                        src={entry.avatar && entry.avatar.trim() ? `https://hypecircle-storage-bucket.s3.amazonaws.com/public/${entry.avatar}` : undefined}
                        alt={entry.displayName || entry.username}
                        sx={{
                          width: isMobile ? 50 : 56,
                          height: isMobile ? 50 : 56,
                          border: entry.rank <= 3 ? '3px solid rgba(255,255,255,0.5)' : '2px solid transparent',
                          boxShadow: entry.rank <= 3 ? theme.shadows[8] : theme.shadows[2]
                        }}
                      >
                        <Person />
                      </Avatar>
                      {entry.badgeCount > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -4,
                            right: -4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            border: '2px solid white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: 'white'
                          }}
                        >
                          {entry.badgeCount}
                        </Box>
                      )}
                    </ListItemAvatar>

                    {/* User Info */}
                    <ListItemText
                      sx={{ flex: 1, minWidth: 0 }}
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography 
                            variant={isMobile ? "subtitle2" : "subtitle1"} 
                            fontWeight={600}
                            sx={{ 
                              color: entry.rank <= 3 ? 'white' : 'text.primary',
                              textShadow: entry.rank <= 3 ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none'
                            }}
                          >
                            {entry.displayName || entry.username}
                          </Typography>
                          {entry.title && (
                            <Chip 
                              label={entry.title} 
                              size="small" 
                              variant="outlined"
                              sx={{
                                color: entry.rank <= 3 ? 'white' : 'text.primary',
                                borderColor: entry.rank <= 3 ? 'rgba(255,255,255,0.5)' : 'divider',
                                bgcolor: entry.rank <= 3 ? 'rgba(255,255,255,0.1)' : 'transparent'
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography 
                          component="div"
                          variant="body2"
                        >
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={isMobile ? 1 : 2} 
                            mt={0.5}
                            flexWrap="wrap"
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <EmojiEvents 
                                fontSize="small" 
                                sx={{ color: entry.rank <= 3 ? 'rgba(255,255,255,0.8)' : 'primary.main' }}
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: entry.rank <= 3 ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                                  fontWeight: 500
                                }}
                              >
                                {entry.achievementCount} achievements
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Star 
                                fontSize="small" 
                                sx={{ color: entry.rank <= 3 ? 'rgba(255,255,255,0.8)' : 'secondary.main' }}
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: entry.rank <= 3 ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                                  fontWeight: 500
                                }}
                              >
                                {entry.leaderboardPoints || 0} pts (season)
                              </Typography>
                            </Box>
                            {entry.badgeCount > 0 && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <WorkspacePremium 
                                  fontSize="small" 
                                  sx={{ color: entry.rank <= 3 ? 'rgba(255,255,255,0.8)' : 'warning.main' }}
                                />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: entry.rank <= 3 ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                                    fontWeight: 500
                                  }}
                                >
                                  {entry.badgeCount} badges
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Typography>
                      }
                    />
                  </ListItem>
                </Zoom>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AchievementLeaderboards;